// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

export * from './types';
export * from './payloadTypes';
import moment from 'moment';
import {
    all,
    call,
    put,
    takeEvery,
    takeLatest
} from 'redux-saga/effects';

import history from 'src/history';

import serviceRequest from './../../../dal/ServiceRequestDal';

import {fetchDeviceInfo} from './../../sagas/device/deviceInfoSagas';
import { ILineItem, IPayload, IPatchAPIPayload } from './../../sagas/serviceRequest/payloadTypes';
import { IClaimOrder } from './../../sagas/serviceRequest/types';

import {
    actionStatusStarted,
    actionStatusFailed,
    actionStatusSuccess,
} from './../../../store/actionStatus';

import {
    CANCEL_LINE_ITEM,
    GET_SERVICE_REQUEST_DATA,
    GET_BATCH_SERVICE_REQUEST_DATA,
    getServiceRequestDataFail,
    getServiceRequestDataSuccess,
    UPDATE_ONSITE_ORDER_DETAILS,
} from './../../../store/deviceInfo/claimOrder';

import {
    EMAIL_SHIPPING_LABEL,
    emailShippingLabelSuccess,
    GET_SHIPPING_LABEL,
    getShippingLabelSuccess,
    REGENERATED_SHIPPING_LABEL,
    showOrderUpdateAlert,
    PUT_RECONCILE_SHIPPING_LABEL,
    GET_RECONCILE_USERS,
    getReconcileUsersSuccess,
    putReconcileShippingLabelSuccess,
    putReconcileShippingLabelFailure
} from './../../../store/deviceInfo/actions';

// -------------------------------------------------------------------
// ------------------------------ SAGAS ------------------------------
// -------------------------------------------------------------------

export function* fetchServiceRequestData(request: any) {
    const serviceRequestId: string = request.payload;

    try {
        yield put(actionStatusStarted({actionName: GET_SERVICE_REQUEST_DATA}));
        const responseData: IPayload = yield call(serviceRequest.getServiceRequest, serviceRequestId);

        const { items, paymentDetails } = responseData;

        yield fetchDeviceInfo({ payload:{
                country: paymentDetails.billTo.country,
                serialNumber: items[0].deviceInfo.serialNumber,
                noReroute: true,
                noBatchCall: true,
            }});

        yield put(actionStatusSuccess({actionName: GET_SERVICE_REQUEST_DATA}));
        yield put(getServiceRequestDataSuccess(parsePayload(responseData)));
        history.push(`/device/orderDetail`);
    } catch (e) {
        yield put(getServiceRequestDataFail(e));
        yield put(actionStatusFailed({
            actionName: GET_SERVICE_REQUEST_DATA,
            data: {
                serviceRequestId,
            },
            error: e,
        }));
    }
}

export function* fetchBatchServiceRequestData(request: any) {
    const serviceRequestIds: string[] = request.payload || [];

    try {
        yield put(actionStatusStarted({actionName: GET_BATCH_SERVICE_REQUEST_DATA}));

        // TODO: change to bulkService once bulkService is ready
        //const responseData: IPayload = yield call(serviceRequest.getBulkServiceRequest, serviceRequestIds.join(','));
        const responseData: any[] = yield all<any[]>(serviceRequestIds.map((serviceRequestId: string) => {
            return call(serviceRequest.getServiceRequest, serviceRequestId) as any;
        }));

        const parsedResponse: any[] = responseData.map((claim) => parsePayload(claim));

        yield put(getServiceRequestDataSuccess(parsedResponse));
        yield put(actionStatusSuccess({actionName: GET_BATCH_SERVICE_REQUEST_DATA}));
    } catch (e) {
        yield put(actionStatusFailed({
            actionName: GET_BATCH_SERVICE_REQUEST_DATA,
            data: {
                serviceRequestIds,
            },
            error: e,
        }));
    }
}

export function* cancelLineItem (request: any) {
    const {
        claimOrderId,
        lineItemId,
        serviceNotificationNumber,
    } = request.payload;
    const actionName = `${CANCEL_LINE_ITEM}-${lineItemId}`;

    try {
        yield put(actionStatusStarted({actionName}));
        if (serviceNotificationNumber) {
            yield call(serviceRequest.cancelLineItemByServiceNotification, { claimOrderId, serviceNotificationNumber });
        } else {
            yield call(serviceRequest.cancelLineItem, { claimOrderId, lineItemId });
        }
        yield put(actionStatusSuccess({actionName}));
    } catch (e) {
        yield put(actionStatusFailed({
            actionName,
            data: {
                ...request,
            },
            error: e,
        }));
    }
}

export function* emailShippingLabel (request: any) {
    const {
        serviceRequestNumber: incidentNumber = 0,
        serialNumber = '',
    } = request.payload;
    const actionName = `${EMAIL_SHIPPING_LABEL}-${serialNumber}`;

    try {
        yield put(actionStatusStarted({actionName}));
        // @ts-ignore
        yield call(serviceRequest.emailShippingLabel, {
            incidentNumber,
            serialNumber,
        });

        yield put(actionStatusSuccess({actionName}));
        yield put(emailShippingLabelSuccess());
    } catch (e) {
        yield put(actionStatusFailed({
            actionName,
            data: {
                ...request,
            },
            error: e,
        }));
    }
}

export function* getShippingLabel (request: any) {
    const { lineItemId, claimOrderId } = request.payload;
    const actionName = `${GET_SHIPPING_LABEL}-${lineItemId}`;

    yield put(actionStatusStarted({actionName}));
    try {
        const data = yield call(serviceRequest.getShippingLabel, {
            lineItemId,
            claimOrderId,
        });

        if (typeof(data) !== 'object' && !data.shippingLabelData) {
            throw new Error('get shipping label failed');
        }

        yield put(actionStatusSuccess({actionName}));
        yield put(getShippingLabelSuccess({ lineItemId, data }));
    } catch (e) {
        yield put(actionStatusFailed({
            actionName,
            data: {
                ...request,
            },
            error: e,
        }));
    }
}
export function* generateShippingLabel (request: any) {
    const { lineItemId, claimOrderId } = request.payload;
    const actionName = `${GET_SHIPPING_LABEL}-${lineItemId}`;

    yield put(actionStatusStarted({actionName}));
    try {
        const data = yield call(serviceRequest.regenerateShippingLabel, {
            lineItemId,
            claimOrderId,
        });

        if (typeof(data) !== 'object' && !data.shippingLabelData) {
            throw new Error('regenerate shipping label failed');
        }

        yield put(actionStatusSuccess({actionName}));
        yield put(getShippingLabelSuccess({ lineItemId, data }));
    } catch (e) {
        yield put(actionStatusFailed({
            actionName,
            data: {
                ...request,
            },
            error: e,
        }));
    }
}

export function* putReconcileShippingLabel (request: any) {
    const actionName = `${PUT_RECONCILE_SHIPPING_LABEL}-${request.payload}`;

    yield put(actionStatusStarted({actionName}));
    try {
        const data = yield call(serviceRequest.putReconcileShippingLabel, request.payload);
        yield put(actionStatusSuccess({actionName}));
        yield put(putReconcileShippingLabelSuccess(data));
    } catch (e) {
        yield put(actionStatusFailed({
            actionName,
            data: {
                ...request,
            },
            error: e,
        }));
        yield put(putReconcileShippingLabelFailure(e));
    }
}

export function* getReconcileUsers () {
     const actionName = `${GET_RECONCILE_USERS}`;

    yield put(actionStatusStarted({actionName}));
    try {
        const data = yield call(serviceRequest.getReconcileUsers);
        yield put(actionStatusSuccess({actionName}));
        yield put(getReconcileUsersSuccess(data));
    } catch (e) {
        yield put(actionStatusFailed({
            actionName,
            error: e,
        }));
    }
}

export function* updateOnsiteOrderDetails (request: any) {
    const { claimOrderId } = request.payload;
    const payload = convertPatchPayload(request.payload);

    try {
        yield put(actionStatusStarted({actionName: `${UPDATE_ONSITE_ORDER_DETAILS}-${claimOrderId}`}));
        const responseData: IPayload = yield call(serviceRequest.onSiteOrderPatchUpdate, claimOrderId, payload);

        yield put(getServiceRequestDataSuccess(parsePayload(responseData)));
        yield put(actionStatusSuccess({actionName: `${UPDATE_ONSITE_ORDER_DETAILS}-${claimOrderId}`}));
        yield put(showOrderUpdateAlert());
    } catch (e) {
        yield put(getServiceRequestDataFail(e));
        yield put(actionStatusFailed({
            actionName: `${UPDATE_ONSITE_ORDER_DETAILS}-${claimOrderId}`,
            data: {
                payload,
            },
            error: e,
        }));
    }
}


export default [
    takeEvery(CANCEL_LINE_ITEM, cancelLineItem),
    takeEvery(EMAIL_SHIPPING_LABEL, emailShippingLabel),
    takeEvery(GET_SHIPPING_LABEL, getShippingLabel),
    takeLatest(GET_SERVICE_REQUEST_DATA, fetchServiceRequestData),
    takeLatest(GET_BATCH_SERVICE_REQUEST_DATA, fetchBatchServiceRequestData),
    takeEvery(REGENERATED_SHIPPING_LABEL, generateShippingLabel),
    takeEvery(PUT_RECONCILE_SHIPPING_LABEL, putReconcileShippingLabel),
    takeEvery(GET_RECONCILE_USERS,getReconcileUsers),
    takeLatest(UPDATE_ONSITE_ORDER_DETAILS, updateOnsiteOrderDetails),
];

// -------------------------------------------------------------------
// ----------------------------- HELPERS -----------------------------
// -------------------------------------------------------------------


export enum LineItemStatusHierarchy {
    closed = 0,
    cancelled = 1,
    checkout = 2,
    rejected = 3,
    new = 4,
    cancelpending = 5,
    open = 6,
    fulfilled = 7,
    completepending = 8,
}

// filter all status to either open or closed
function identifyStatus(status: string): string {
    switch (LineItemStatusHierarchy[status.toLowerCase()]) {
        case LineItemStatusHierarchy.cancelled:
        case LineItemStatusHierarchy.closed:
        case LineItemStatusHierarchy.checkout:
        case LineItemStatusHierarchy.rejected:
        case LineItemStatusHierarchy.fulfilled:
            return 'closed';
        case LineItemStatusHierarchy.completepending:
            return 'inprogress';
        case LineItemStatusHierarchy.cancelpending:
        case LineItemStatusHierarchy.open:
        case LineItemStatusHierarchy.new:
        default:
            return 'open';
    }
}

export function parsePayload(payload: IPayload): IClaimOrder {
    const SERVICE_REQUEST_ID = 'serviceRequestId';
    const serviceRequests = {};

    (payload && payload.items || []).forEach((lineItem: ILineItem) => {
        const serviceRequestNumber = lineItem[SERVICE_REQUEST_ID];
        if (!serviceRequests[serviceRequestNumber]) {
            serviceRequests[serviceRequestNumber] = {
                serviceRequestNumber,
                status: lineItem.progress.name === 'InInventory' ? 'closed' : identifyStatus(lineItem.status),
                lineItems: [],
            };
        }
        const serviceRequest = serviceRequests[serviceRequestNumber];
        // determine service request's status base on each line item's status
        // the status with the higher hierarchy will persist for the service request.
        if (LineItemStatusHierarchy[serviceRequest.status] < LineItemStatusHierarchy[lineItem.status]) {
            serviceRequest.status = lineItem.progress.name === 'InInventory' ? 'closed' : identifyStatus(lineItem.status);
        }
        serviceRequest.lineItems.push(lineItem);
    });

    return {
        claimOrderId: payload.claimOrderId,
        createdDate: payload.createdDate,
        customerLocale: payload.customerLocale,
        lineItems: payload.items,
        payments: payload.paymentDetails,
        serviceRequests: Object.values(serviceRequests),
        shipments: payload.shipmentDetails,
        status: payload.status,
        tier: payload.tier,
        reasonForNoRepair: payload.reasonForNoRepair,
        reasonForNoRepairDescription:payload.reasonForNoRepairDescription,
        serviceLocationDetails: payload.serviceLocationDetails,
        customerId: payload.customerId,
    };
}

function convertPatchPayload(payload: any): IPatchAPIPayload {
    const { partnerDetails, appointmentDetails, partnerComments, partnerReferenceNumber, confirmedAppointmentSchedule } = payload;
    partnerDetails.partnerComments = partnerComments;
    partnerDetails.partnerReferenceNumber = partnerReferenceNumber;
    appointmentDetails.confirmedAppointmentSchedule = moment(confirmedAppointmentSchedule).format();

    return {
        partnerDetails,
        appointmentDetails,
        items: [
            {
                partnerDetails,
                appointmentDetails,
            },
        ],
    };
}


